import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import useDebounce from '@reverbdotcom/commons/src/components/useDebounce';

const MINIMUM_PRICE_CENTS = 99;
export const PRICE_DEBOUNCE_MS = 300;

export const ESTIMATED_EARNINGS_QUERY = gql(`
  query Core_RenewListing_EstimatedEarnings(
    $listingId: String,
    $amount: String!,
    $currency: String!,
    $payoutToWallet: Boolean
  ) {
    estimatedEarnings(input:{
      listingId: $listingId,
      listingPrice: {
        amount: $amount
        currency: $currency
      }
      payoutToWallet: $payoutToWallet
    }) {
      feeLineItems {
        fee {
          amount
          display
          amountCents
        }
        type
        label
        tooltipText
      }
      estimatedEarnings {
        amount
        display
        amountCents
      }
      estimatedEarningsTooltipText
    }
  }
`);

export function useEstimatedEarnings({
  listingId,
  price,
  currency,
  payoutToWallet,
}: {
  listingId: string;
  price: string;
  currency: string;
  payoutToWallet?: boolean;
}) {
  const debouncedPrice = useDebounce(price, PRICE_DEBOUNCE_MS);
  const priceAmountCents = parseAmount(debouncedPrice, currency).amountCents;

  const { data, previousData, loading, errors } = useQuery(
    ESTIMATED_EARNINGS_QUERY,
    {
      variables: { listingId, amount: debouncedPrice, currency, payoutToWallet },
      skip: !debouncedPrice || !currency || priceAmountCents <= MINIMUM_PRICE_CENTS,
    },
  );

  return {
    data,
    previousData,
    // A price that hasn't settled yet means the current data is for an older price
    loading: loading || debouncedPrice !== price,
    errors,
  };
}
